import { useState } from 'react'
import { Navigate } from 'react-router'

import appleStore from '@/assets/marketing/fuelapp/apple-store.svg'
import findFuel from '@/assets/marketing/fuelapp/find-fuel.png'
import fuelCode from '@/assets/marketing/fuelapp/fuel-code.svg'
import fuelAppLogo from '@/assets/marketing/fuelapp/fuelapp-logo.svg'
import fuelAppLogoInverted from '@/assets/marketing/fuelapp/fuelapp-logo-inverted.svg'
import gasPump from '@/assets/marketing/fuelapp/gas-pump.jpg'
import googlePlay from '@/assets/marketing/fuelapp/google-play.svg'
import izakVenuti from '@/assets/marketing/fuelapp/izak-venuti.jpg'
import mapHero from '@/assets/marketing/fuelapp/map-hero.png'
import { Anchor } from '@/components/ui/Anchor'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { cn } from '@/lib/utils'

/* One-off standalone landing page for FuelApp (moved from the HubSpot page at
   cai.ai/fuelapp). It ships its own FuelApp-branded header/footer and does NOT
   use the shared marketing chrome (MarketingLayout / SiteNav / SiteFooter). */

const HERO_CHECKS = ['Free download', 'Zero fees', 'No credit check', 'No minimum spend']

const STEPS = [
  {
    title: 'Find fuel discounts',
    body: 'Open up FuelApp to see discounted pricing at stations near you.',
    image: findFuel,
    alt: 'FuelApp map screen showing discounted stations',
    reversed: false,
    photo: false,
  },
  {
    title: 'Get payment code',
    body: "Head over to the station, then tap 'Get fuel code' in FuelApp. Your code will be valid for 30 minutes.",
    image: fuelCode,
    alt: 'FuelApp payment code screen',
    reversed: true,
    photo: false,
  },
  {
    title: 'Enter code at the pump to pay',
    body: 'No need to go inside the station, so you can fuel up fast and get back on the road.',
    image: gasPump,
    alt: 'A driver entering a fuel code on the pump keypad',
    reversed: false,
    photo: true,
  },
]

const FAQS: { question: string; answer: React.ReactNode[] }[] = [
  {
    question: 'How do I pay for fuel on FuelApp?',
    answer: [
      "Upload any debit card, credit card, or fuel card as your payment method. When you pay with a fuel code, we'll charge the amount you pump to the card on file.",
    ],
  },
  {
    question: 'Are there any fees to use FuelApp?',
    answer: [
      'Nope! FuelApp is free to download, and there are no membership fees or credit card processing fees.',
    ],
  },
  {
    question: 'How do payment holds work?',
    answer: [
      'When you request a fuel code, a hold is placed on your payment method for the maximum amount you can use your fuel code to pump.',
      "By default, this is set to $350, but you can update it to any amount between $50-800. Before you request a code, find a location, tap 'Fuel amount $350 >' below the 'Get fuel code' button, enter the amount you want to authorize, and tap 'Confirm.'",
      'After you fuel, the hold will update to the amount you actually pumped. If you cancel the fuel code or let it expire, the hold will come off your payment method within a few hours.',
    ],
  },
  {
    question: 'Where can I see transactions and receipts?',
    answer: ["Find these under the 'Purchases' tab."],
  },
  {
    question: 'How does the FuelApp Referral Program work?',
    answer: [
      'Share a referral code with friends. Every driver who signs up with your code and pumps 75+ gallons earns $15 for you and $15 for them.',
      <>
        Learn more at{' '}
        <Anchor href="/fuelapp/referrals" className="font-bold text-[#050A12] hover:text-[#DB9200]">
          cai.ai/fuelapp/referrals
        </Anchor>
        .
      </>,
    ],
  },
  {
    question: 'I have more questions. Who can I talk to?',
    answer: ['Give us a call at (469) 459-5311 or email us at support@fuel.app.'],
  },
]

function CheckIcon() {
  return (
    <span className="inline-flex size-5 flex-none items-center justify-center rounded-full bg-[#31AB6A]">
      <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
        <path
          d="M2.2 6.4L4.7 8.9 9.8 3.3"
          stroke="#fff"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

const NAV_LINK = 'text-[15px] font-bold text-[#535C67] transition-colors hover:text-[#050A12]'

function DownloadLink({ large, cta, className }: { large?: boolean; cta: string; className?: string }) {
  return (
    <a
      href="#download"
      onClick={() => trackMarketingCta(cta)}
      className={cn(
        'inline-block font-bold tracking-[-0.01em] text-[#050A12] transition-[background,transform] duration-180 ease-in-out hover:bg-[#DB9200]',
        large
          ? 'rounded-xl bg-[#F6B100] px-9 py-4 text-[17px] shadow-[0_12px_28px_-10px_rgba(246,177,0,0.7)] hover:-translate-y-0.5'
          : 'rounded-[10px] bg-[#F6B100] px-5.5 py-[11px] text-[15px] hover:-translate-y-px',
        className
      )}
    >
      Download
    </a>
  )
}

function FaqItem({
  question,
  answer,
  open,
  onToggle,
}: {
  question: string
  answer: React.ReactNode[]
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-b border-[#E1E7EE]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-6 px-1 py-6 text-left"
      >
        <span className="text-[19px] font-bold tracking-[-0.01em] text-[#050A12]">{question}</span>
        <span
          className={cn(
            'inline-block flex-none font-mono text-[28px] leading-none text-[#F6B100] transition-transform duration-300',
            open && 'rotate-45'
          )}
        >
          +
        </span>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out',
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="flex max-w-[62ch] flex-col gap-4 px-1 pb-6.5">
          {answer.map((paragraph, i) => (
            <p key={i} className="text-base leading-[1.6] text-[#535C67]">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}

export function FuelApp() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const { redirecting, navigateTo } = useHostGuard('marketing')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  return (
    <div className="bg-surface fuelapp-brand min-h-screen w-full shrink-0 font-sans text-[#050A12] antialiased">
      <title>FuelApp by Cai Labs</title>
      <meta
        name="description"
        content="Big networks. Bigger discounts. Right at the pump. Save up to $1/gal on diesel at leading truck stop networks nationwide."
      />

      <header className="sticky top-0 z-50 border-b border-[#E1E7EE] bg-white/80 backdrop-blur-[14px]">
        <div className="mx-auto flex max-w-[1180px] items-center justify-between gap-6 px-8 py-[15px] max-md:gap-3 max-md:px-5">
          <img src={fuelAppLogo} alt="FuelApp" className="block h-7.5 w-auto flex-none" />
          <nav className="flex items-center gap-7.5 max-md:gap-3">
            <Anchor href="#how" className={cn(NAV_LINK, 'max-md:hidden')}>
              How it works
            </Anchor>
            <Anchor href="#faq" className={cn(NAV_LINK, 'max-md:hidden')}>
              FAQ
            </Anchor>
            <Anchor
              href="/fuelapp/teams"
              onClick={() => trackMarketingCta('fuelapp_nav_teams')}
              className={cn(NAV_LINK, 'whitespace-nowrap max-[374px]:hidden')}
            >
              For teams
            </Anchor>
            <DownloadLink cta="fuelapp_download_header" className="max-md:px-4" />
          </nav>
        </div>
      </header>

      <section className="mx-auto flex max-w-[1180px] flex-col items-center px-8 pt-20 pb-12 text-center max-md:px-5 max-md:pt-14 max-md:pb-9">
        <h1 className="max-w-[18ch] text-[56px] leading-[1.03] font-extrabold tracking-[-0.035em] text-balance max-md:text-[38px]">
          Big networks. Bigger discounts. Right at the pump.
        </h1>
        <p className="mt-6 max-w-[52ch] text-[19px] leading-[1.55] text-[#535C67]">
          Save up to $1/gallon at the nation's largest truck stop networks. Pay at the pump and get on the
          road.
        </p>
        <div className="relative mt-11 w-full max-w-225 overflow-hidden rounded-3xl border border-[#E1E7EE] bg-[#F0F4F9] pt-14 shadow-[0_30px_70px_-28px_rgba(5,10,18,0.32)]">
          <img
            src={mapHero}
            alt="FuelApp map showing fuel discounts near you"
            className="block h-auto w-full"
          />
        </div>
        <div className="mt-9 flex flex-wrap justify-center gap-x-7.5 gap-y-3.5">
          {HERO_CHECKS.map((label) => (
            <div key={label} className="flex items-center gap-2.5">
              <CheckIcon />
              <span className="font-mono text-[13px] font-medium text-[#21272F]">{label}</span>
            </div>
          ))}
        </div>
        <DownloadLink large cta="fuelapp_download_hero" className="mt-8.5" />
      </section>

      <section className="mx-auto max-w-[1180px] px-8 pt-4 pb-18 max-md:px-5 max-md:pb-12">
        <div className="flex items-center gap-7 rounded-[22px] border border-[#FBEEC9] bg-[#FCF7E7] px-10 py-8.5 max-md:flex-col max-md:items-start max-md:px-6 max-md:py-7">
          <img
            src={izakVenuti}
            alt="Izak Venuti"
            className="size-22 flex-none rounded-full border-[3px] border-[#FAD06F] object-cover"
          />
          <div>
            <p className="text-[23px] leading-[1.4] font-bold tracking-[-0.01em] text-pretty">
              "I saved a lot this past week. Discounts are pretty good. Give it a try. It's worth it."
            </p>
            <p className="mt-3.5 font-mono text-[13px] tracking-[0.01em] text-[#884A03]">
              — Izak Venuti, Giovanni Trucking LLC
            </p>
          </div>
        </div>
      </section>

      <section id="how" className="border-y border-[#E1E7EE] bg-[#F8FAFE]">
        <div className="mx-auto max-w-[1180px] px-8 py-21 max-md:px-5 max-md:py-14">
          <h2 className="mb-14 text-[40px] font-extrabold tracking-[-0.03em]">How it works</h2>
          <div className="flex flex-col gap-16 max-md:gap-7">
            {STEPS.map((step, i) => (
              <div
                key={step.title}
                className="grid grid-cols-2 items-center gap-14 max-md:grid-cols-1 max-md:gap-7"
              >
                <div
                  className={cn(
                    'bg-surface flex h-90 items-center justify-center overflow-hidden rounded-4xl border border-[#E1E7EE] max-md:h-65',
                    step.photo ? 'p-0' : 'p-7',
                    step.reversed && 'order-2 max-md:order-0'
                  )}
                >
                  <img
                    src={step.image}
                    alt={step.alt}
                    className={cn('block', step.photo ? 'h-full w-full object-cover' : 'max-h-full w-auto')}
                  />
                </div>
                <div className={cn(step.reversed && 'order-1 max-md:order-0')}>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex size-7.5 flex-none items-center justify-center rounded-[9px] bg-[#050A12] font-mono text-sm font-bold text-white">
                      {i + 1}
                    </span>
                    <h3 className="text-[26px] font-extrabold tracking-[-0.02em]">{step.title}</h3>
                  </div>
                  <p className="mt-4 max-w-[46ch] text-lg leading-[1.55] text-[#535C67]">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="mx-auto max-w-[820px] px-8 py-22 max-md:px-5 max-md:py-14">
        <h2 className="text-[40px] font-extrabold tracking-[-0.03em] max-md:text-[32px]">FAQs</h2>
        <div className="mt-7 border-t border-[#E1E7EE]">
          {FAQS.map((faq, i) => (
            <FaqItem
              key={faq.question}
              question={faq.question}
              answer={faq.answer}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </section>

      <section id="download" className="bg-[#F6B100]">
        <div className="mx-auto max-w-[1180px] px-8 py-22 text-center max-md:px-5 max-md:py-16">
          <h2 className="text-[46px] font-extrabold tracking-[-0.03em] text-balance max-md:text-[34px]">
            Don't miss out on major fuel savings
          </h2>
          <p className="mt-4.5 text-xl font-medium text-[#6C380B]">
            Download FuelApp and start saving today.
          </p>
          <div className="mt-9.5 flex flex-wrap justify-center gap-4">
            <a
              href="https://mobile.fuel.app/vFhe2PcwQ6b"
              className="block transition-transform duration-180 hover:-translate-y-0.5"
              onClick={() => trackMarketingCta('fuelapp_app_store')}
            >
              <img src={appleStore} alt="Download on the App Store" className="block h-13.5" />
            </a>
            <a
              href="https://mobile.fuel.app/ghNQSA5vQ6b"
              className="block transition-transform duration-180 hover:-translate-y-0.5"
              onClick={() => trackMarketingCta('fuelapp_google_play')}
            >
              <img src={googlePlay} alt="Get it on Google Play" className="block h-13.5" />
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-neutral-900">
        <div className="mx-auto max-w-[1180px] px-8 pt-14 pb-10 max-md:px-5 max-md:pt-10 max-md:pb-8">
          <img src={fuelAppLogoInverted} alt="FuelApp" className="block h-7 w-auto" />
          <div className="mt-9.5 flex flex-wrap items-center justify-between gap-4 border-t border-[#21272F] pt-6">
            <span className="font-mono text-xs text-[#737D89]">© 2026 FuelApp by Cai Labs</span>
            <div className="flex gap-5">
              <a
                href="/terms"
                className="font-mono text-xs text-[#9BA4AF] no-underline transition-colors duration-150 hover:text-[#F6B100]"
              >
                Terms
              </a>
              <a
                href="/privacy"
                className="font-mono text-xs text-[#9BA4AF] no-underline transition-colors duration-150 hover:text-[#F6B100]"
              >
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
